/**
 * Hook that fetches the saved questionnaire-review record for a single PA
 * order via {@link getQuestionnaireReviewForAnOrder} on demand.
 *
 * Unlike data-loader hooks that fire on mount, this one only triggers when
 * the consumer explicitly calls {@link fetchQuestionnaireReview}. Built on
 * top of {@link useAbortableFetch} so any in-flight request is cancelled
 * automatically on a follow-up call or when the consuming component
 * unmounts.
 *
 * MOCK MODE — until the backend endpoint is live, set
 * {@link USE_MOCK_QUESTIONNAIRE_REVIEW} to `true` to short-circuit the
 * fetcher with a local fixture
 * ({@link ./__mocks__/questionnaireReviewMock.json}). The mock still flips
 * `loading` true → false with an artificial delay so consumer UI flows can
 * be exercised end-to-end. Flip the flag back to `false` when the real
 * endpoint is wired up; no other call-site changes are required.
 *
 * @module pharmaQuestionaire/hooks/useBranchedPharmaQuestions
 */
import { useCallback, useEffect, useState } from "react";
import { getQuestionnaireReviewForAnOrder } from "../api/getCalls/getQuestionnaireReviewForAnOrder";
import { QuestionnaireReviewResponse } from "../data-model/questionnaireReview";
import questionnaireReviewMock from "../pages/pharmaQuestionaire/hooks/__mocks__/questionnaireReviewMock.json";
import { useAbortableFetch } from "./useAbortableFetch";

/**
 * Flip to `false` once the real questionnaire-review endpoint is live.
 * When `true`, `fetchQuestionnaireReview` resolves with the bundled
 * fixture instead of calling the API.
 */
const USE_MOCK_QUESTIONNAIRE_REVIEW = false;

/** Artificial delay (ms) used by the mock to simulate network latency. */
const MOCK_FETCH_DELAY_MS = 400;

/** Bundled fixture — typed against the real response so drift is caught. */
const MOCK_RESPONSE =
  questionnaireReviewMock as unknown as QuestionnaireReviewResponse;

/** Shape returned to consumers of {@link useBranchedPharmaQuestions}. */
interface UseBranchedPharmaQuestionsReturn {
  /** Parsed questionnaire-review envelope, or `null` until the first success. */
  data: QuestionnaireReviewResponse | null;
  /** `true` while a request is in flight. */
  loading: boolean;
  /** Last non-abort error from the API, or `null`. */
  error: Error | null;
  /**
   * Triggers the fetch for the supplied `orderId`. Aborts any previous
   * in-flight request before starting a new one. Resolves to the parsed
   * envelope on success or `null` on failure / cancellation.
   */
  fetchQuestionnaireReview: (
    orderId: string,
  ) => Promise<QuestionnaireReviewResponse | null>;
  /** Aborts any in-flight request. */
  abort: () => void;
}

/**
 * Returns an imperative fetcher plus the latest data / loading / error
 * state for the questionnaire-review endpoint.
 *
 * When an `orderId` is supplied, the hook fetches automatically on mount
 * (and whenever `orderId` changes) and reports `loading: true` on the
 * very first render — before the effect ticks — so consumers don't see a
 * mount-time flicker between "not loading, no data" and "loading".
 * Consumers can still call `fetchQuestionnaireReview` imperatively to
 * refetch.
 *
 * @param orderId - PA order id to auto-fetch on mount; pass `undefined`
 *                  to keep the hook fully imperative
 * @returns Imperative fetcher and request state
 */
export const useBranchedPharmaQuestions = (
  orderId?: string,
): UseBranchedPharmaQuestionsReturn => {
  const fetchReview = useCallback(
    (signal: AbortSignal, id: string) =>
      getQuestionnaireReviewForAnOrder(id, signal),
    [],
  );

  const { runFetch, abort, data, error, loading } =
    useAbortableFetch<QuestionnaireReviewResponse>(fetchReview);

  // ---- Mock mode -------------------------------------------------------
  // Keeps the same { data, loading, error, fetch, abort } surface as the
  // real path so consumers don't have to special-case anything.
  const [mockData, setMockData] = useState<QuestionnaireReviewResponse | null>(
    null,
  );
  const [mockLoading, setMockLoading] = useState<boolean>(false);
  const mockAbortRef = useState<{ controller: AbortController | null }>({
    controller: null,
  })[0];

  const fetchMockQuestionnaireReview = useCallback(
    async (orderId: string): Promise<QuestionnaireReviewResponse | null> => {
      if (!orderId) return null;
      mockAbortRef.controller?.abort();
      const controller = new AbortController();
      mockAbortRef.controller = controller;
      setMockLoading(true);
      try {
        await new Promise<void>((resolve, reject) => {
          const timer = setTimeout(resolve, MOCK_FETCH_DELAY_MS);
          controller.signal.addEventListener("abort", () => {
            clearTimeout(timer);
            reject(Object.assign(new Error("Aborted"), { name: "AbortError" }));
          });
        });
        if (controller.signal.aborted) return null;
        setMockData(MOCK_RESPONSE);
        return MOCK_RESPONSE;
      } catch {
        return null;
      } finally {
        if (mockAbortRef.controller === controller) {
          mockAbortRef.controller = null;
        }
        setMockLoading(false);
      }
    },
    [mockAbortRef],
  );

  const abortMock = useCallback(() => {
    mockAbortRef.controller?.abort();
    mockAbortRef.controller = null;
    setMockLoading(false);
  }, [mockAbortRef]);
  // ----------------------------------------------------------------------

  const fetchQuestionnaireReview = useCallback(
    async (orderId: string): Promise<QuestionnaireReviewResponse | null> => {
      if (USE_MOCK_QUESTIONNAIRE_REVIEW) {
        return fetchMockQuestionnaireReview(orderId);
      }
      if (!orderId) return null;
      const result = await runFetch(orderId);
      return result.success ? result.data : null;
    },
    [runFetch, fetchMockQuestionnaireReview],
  );

  useEffect(
    () => () => {
      if (USE_MOCK_QUESTIONNAIRE_REVIEW) {
        abortMock();
      } else {
        abort();
      }
    },
    [abort, abortMock],
  );

  useEffect(() => {
    if (!orderId) return;
    fetchQuestionnaireReview(orderId);
  }, [orderId, fetchQuestionnaireReview]);

  const normalizedError =
    error && error.name !== "AbortError"
      ? error instanceof Error
        ? error
        : new Error(String(error))
      : null;

  if (USE_MOCK_QUESTIONNAIRE_REVIEW) {
    const isInitialMockLoading = !!orderId && mockData === null && !mockLoading;
    return {
      data: mockData,
      loading: mockLoading || isInitialMockLoading,
      error: null,
      fetchQuestionnaireReview,
      abort: abortMock,
    };
  }

  // Treat "we have an orderId but the auto-fetch hasn't started yet" as
  // loading, so the very first render reports `loading: true` and
  // consumers can render their loader without a one-frame flicker.
  const isInitialAutoLoading =
    !!orderId && data === null && !loading && !normalizedError;

  return {
    data,
    loading: loading || isInitialAutoLoading,
    error: normalizedError,
    fetchQuestionnaireReview,
    abort,
  };
};
